import React from 'react';
import { useTrackImpression, useTrackInteraction } from './hooks';
import { EventTrackingMetaData, TrackInteractionCallback } from './types';
import {
    formatCategoryFromTags,
    formatPaidStatus,
    formatVenueToCityRegion,
} from './utils';

interface EventCardTrackingContext {
    trackInteraction?: TrackInteractionCallback;
    statsigLocationString?: string;
    heapDataAttributes?: {
        paidStatus: string;
        eventId: string;
        category: string | null;
        locationSlug: string | null;
    };
}

const EventCardTrackingContext = React.createContext<EventCardTrackingContext>(
    {},
);

export const useEventCardTrackingContext = () => {
    const value = React.useContext(EventCardTrackingContext);

    if (value === undefined) {
        console.warn(
            'You are using a link or other interactable EventCard element without including EventCardTracking in your content. This can lead to missed analytics and it is highly recommended to include.',
        );
    }

    return value;
};

export interface EventCardTrackingProps {
    eventData: EventTrackingMetaData;
    statsigLocationString?: string;
}

export const EventCardTracking: React.FunctionComponent<
    EventCardTrackingProps
> = (props) => {
    const { eventData, statsigLocationString } = props;
    const { isOnlineEvent, id: eventId, venue, isFree, tags } = eventData;
    const node = React.useRef<HTMLDivElement>(null);

    const trackInteraction = useTrackInteraction(
        node,
        eventData,
        statsigLocationString,
    );

    useTrackImpression(node, trackInteraction);

    const stableContextValue = React.useMemo(() => {
        const paidStatus = formatPaidStatus(isFree);
        const locationSlug = formatVenueToCityRegion({
            venue,
            isOnlineEvent,
        });
        const category = formatCategoryFromTags(tags);
        const heapDataAttributes = {
            paidStatus,
            locationSlug,
            category,
            eventId,
        };
        return { trackInteraction, statsigLocationString, heapDataAttributes };
    }, [
        trackInteraction,
        statsigLocationString,
        isFree,
        venue,
        isOnlineEvent,
        tags,
        eventId,
    ]);

    return (
        <EventCardTrackingContext.Provider value={stableContextValue}>
            <div
                ref={node}
                data-testid="event-card-tracking-layer"
                style={{
                    position: 'absolute',
                    top: '0px',
                    left: '0px',
                    height: '100%',
                    pointerEvents: 'none',
                    width: '100%',
                }}
            />
            {props.children}
        </EventCardTrackingContext.Provider>
    );
};
